import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Pre-initialise dataLayer + gtag shim IMMEDIATELY at module load — BEFORE any React
// mount and long before the axios call to /api/public/site-config resolves. Any
// `window.gtag(…)` invoked between now and script arrival is buffered in the dataLayer
// and re-emitted when the real gtag.js loads. Without this, race conditions swallow
// view_item / add_to_cart / begin_checkout events that fire during the first paint.
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
}

/**
 * Loads Google Analytics 4 (gtag.js) once — if a Measurement ID is configured in the
 * admin site_config — and fires a `page_view` event on every route change. The rest of
 * the funnel (view_item, add_to_cart, begin_checkout, search) is mirrored automatically
 * from `window.audrivioTrack` (see MetaPixel.jsx), so call sites don't need to know GA4
 * exists — they just fire once and both platforms receive it with matching semantics.
 *
 * Purchase is intentionally NOT sent from the client: the backend fires it via the
 * Measurement Protocol as soon as the order is paid (`_ga4_send_purchase`) so totals
 * always reflect the authoritative order value even if the browser drops the request.
 */
export default function GoogleAnalytics() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/site-config`).then((r) => {
      if (cancelled) return;
      const mid = (r.data?.ga4_measurement_id || "").trim();
      if (!mid || window.__audrivio_ga4_loaded) return;
      window.__audrivio_ga4_loaded = true;

      // Load gtag.js — dataLayer.push calls made before this arrive are replayed by
      // gtag when it initialises, so early view_item / add_to_cart events land in GA4.
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(mid)}`;
      document.head.appendChild(s);

      window.gtag("js", new Date());
      // send_page_view=false because we fire page_view manually on route change to
      // capture SPA navigations correctly.
      window.gtag("config", mid, { send_page_view: false });

      // gtag sets the _ga cookie on first pageview → grab it right after and also on
      // route change so the checkout can attach it to the order → backend uses it to
      // send the Purchase event via Measurement Protocol stitched to the visitor.
      const grabClientId = () => {
        try {
          const m = document.cookie.match(/(?:^|;\s*)_ga=GA1\.\d+\.([0-9]+\.[0-9]+)/);
          if (m) sessionStorage.setItem("audrivio_ga_client_id", m[1]);
        } catch { /* storage disabled */ }
      };
      setTimeout(grabClientId, 800);

      // Fire the initial page_view (SPA-safe)
      window.gtag("event", "page_view", {
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_title: document.title,
    });
    // Re-grab client_id on every route change (in case cookies became available late)
    try {
      const m = document.cookie.match(/(?:^|;\s*)_ga=GA1\.\d+\.([0-9]+\.[0-9]+)/);
      if (m) sessionStorage.setItem("audrivio_ga_client_id", m[1]);
    } catch { /* ignore */ }
  }, [location.pathname, location.search]);

  return null;
}
